import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  SaveInvoiceTransaction,
  UpdateInvoiceTransaction,
} from "../../services/InvoiceService";
import { NotificationManager } from "react-notifications";

const AddEditInvoiceTransactionDialog = ({
  open,
  onClose,
  onSave,
  selectedTransaction,
  isEditing
}) => {
  const [orderNumber, setOrderNumber] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [sampleNumber, setSampleNumber] = useState(null);
  const [itemNumber, setItemNumber] = useState(null);
  const [glCode, setGlCode] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(null);
  const [unitPrice, setUnitPrice] = useState(null);
  const [listPrice, setListPrice] = useState(null);
  const [adjustedPrice, setAdjustedPrice] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(null);
  const [surchargePercent, setSurchargePercent] = useState(null);
  const [miscChg, setMiscChg] = useState(false);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [sampleIdentity, setSampleIdentity] = useState("");
  const [methodCode, setMethodCode] = useState("");
  const [testCode, setTestCode] = useState("");
  const [jobId, setJobId] = useState("");
  const [recordid, setRecordid] = useState(0);

  useEffect(() => {
    if (selectedTransaction) {
      setOrderNumber(selectedTransaction.orderNumber || null);
      setInvoiceNumber(selectedTransaction.invoiceNumber || "");
      setSampleNumber(selectedTransaction.sampleNumber || null);
      setItemNumber(selectedTransaction.itemNumber || null);
      setGlCode(selectedTransaction.glCode || "");
      setDescription(selectedTransaction.description || "");
      setQuantity(selectedTransaction.quantity || null);
      setUnitPrice(selectedTransaction.unitPrice || null);
      setListPrice(selectedTransaction.listPrice || null);
      setAdjustedPrice(selectedTransaction.adjustedPrice || null);
      setDiscountPercent(selectedTransaction.discountPercent || null);
      setSurchargePercent(selectedTransaction.surchargePercent || null);
      setMiscChg(selectedTransaction.miscChg || false);
      setApplyDiscount(selectedTransaction.applyDiscount || false);
      setSampleIdentity(selectedTransaction.sampleIdentity || "");
      setMethodCode(selectedTransaction.methodCode || "");
      setTestCode(selectedTransaction.testCode || "");
      setJobId(selectedTransaction.jobId || "");
      setRecordid(selectedTransaction.recordid || 0);
    } else {
      resetForm();
    }
  }, [selectedTransaction]);

  const resetForm = () => {
    setOrderNumber(null);
    setInvoiceNumber("");
    setSampleNumber(null);
    setItemNumber(null);
    setGlCode("");
    setDescription("");
    setQuantity(null);
    setUnitPrice(null);
    setListPrice(null);
    setAdjustedPrice(null);
    setDiscountPercent(null);
    setSurchargePercent(null);
    setMiscChg(false);
    setApplyDiscount(false);
    setSampleIdentity("");
    setMethodCode("");
    setTestCode("");
    setJobId("");
    setRecordid(0);
  };

  const parseData = () => {
    return {
      orderNumber: orderNumber ? parseInt(orderNumber, 10) : null,
      invoiceNumber,
      sampleNumber: sampleNumber ? parseInt(sampleNumber, 10) : null,
      itemNumber: itemNumber ? parseInt(itemNumber, 10) : null,
      glCode,
      description,
      quantity: quantity ? parseFloat(quantity) : null,
      unitPrice: unitPrice ? parseFloat(unitPrice) : null,
      listPrice: listPrice ? parseFloat(listPrice) : null,
      adjustedPrice: adjustedPrice ? parseFloat(adjustedPrice) : null,
      discountPercent: discountPercent ? parseFloat(discountPercent) : null,
      surchargePercent: surchargePercent ? parseFloat(surchargePercent) : null,
      miscChg,
      applyDiscount,
      sampleIdentity,
      methodCode,
      testCode,
      jobId,
      recordid
    };
  };

  const handleSave = async () => {
    try {
      const transactionData = parseData();

      let response;
      if (isEditing) {
        response = await UpdateInvoiceTransaction(transactionData);
        NotificationManager.success("Invoice Transaction updated successfully");
      } else {
        response = await SaveInvoiceTransaction(transactionData);
        NotificationManager.success("Invoice Transaction saved successfully");
      }

      if (response.status === 200) {
        onSave();
        onClose();
      } else {
        NotificationManager.error("Error saving/updating Invoice Transaction");
      }
    } catch (error) {
      NotificationManager.error("Error saving/updating Invoice Transaction");
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>
        {selectedTransaction ? "Edit Invoice Transaction" : "Add New Invoice Transaction"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Order Number"
              value={orderNumber || ""}              
              onChange={(e) => setOrderNumber(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Invoice Number"
              value={invoiceNumber}              
              onChange={(e) => setInvoiceNumber(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Sample Number"
              value={sampleNumber || ""}
              onChange={(e) => setSampleNumber(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Item Number"
              value={itemNumber || ""}
              onChange={(e) => setItemNumber(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="GL Code"
              value={glCode}
              onChange={(e) => setGlCode(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Quantity"
              value={quantity || ""}
              onChange={(e) => setQuantity(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Unit Price"
              value={unitPrice || ""}
              onChange={(e) => setUnitPrice(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="List Price"
              value={listPrice || ""}
              onChange={(e) => setListPrice(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Adjusted Price"
              value={adjustedPrice || ""}
              onChange={(e) => setAdjustedPrice(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Discount Percent"
              value={discountPercent || ""}
              onChange={(e) => setDiscountPercent(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Surcharge Percent"
              value={surchargePercent || ""}
              onChange={(e) => setSurchargePercent(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Sample Identity"
              value={sampleIdentity}
              onChange={(e) => setSampleIdentity(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Method Code"
              value={methodCode}
              onChange={(e) => setMethodCode(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Test Code"
              value={testCode}
              onChange={(e) => setTestCode(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Job ID"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={miscChg}
                  onChange={(e) => setMiscChg(e.target.checked)}
                />
              }
              label="Misc Charge"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyDiscount}
                  onChange={(e) => setApplyDiscount(e.target.checked)}
                />
              }
              label="Apply Discount"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {isEditing ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditInvoiceTransactionDialog;
